import useRetrieveStaticDatasets from '@/hooks/api/datasets/static/useRetrieveStaticDatasets.ts';
import { staticSelectionsForEdit } from '@/hooks/api/datasets/static/staticRevisionSelection.ts';
import { get_editor_local_subflow_guarded_field_url, get_update_step_url } from '@/utils/urls.ts';
import { useState } from 'react';
import { useEditVersionContext } from '@/context/EditVersionContext/useEditVersionContext.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { notifyStepMutationError } from '@/services/stepMutationErrors.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';
import useCompositionOperations, {
  CompositionOperation,
} from '@/hooks/api/automations/useCompositionOperations.ts';
import useRetrieveEditorCompositionCapabilities from '@/hooks/api/automations/useRetrieveEditorCompositionCapabilities.ts';
import { DataInput } from '@/types/automationFields.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';

type updateStepParams = {
  step_id: string;
  step_data: object;
};

type GuardedFieldResponse = {
  current_value_token: string | null;
};

const useUpdateStepAPI = () => {
  const apiClient = useApiClient();
  const automationId = useAutomationId();
  const { addToQueue, reportLatestDraftChanged, editingVersion } = useEditVersionContext();
  const { data: staticSources } = useRetrieveStaticDatasets();
  const [pendingCount, setPendingCount] = useState(0);
  const { target } = useActiveCompositionTarget();
  const { mutate: applyComposition, isPending: isCompositionPending } = useCompositionOperations();
  const { data: moduleCapabilities, isLoading: moduleCapabilitiesLoading } =
    useRetrieveEditorCompositionCapabilities(
      target.scope_kind === 'local_subflow' ? target.subflow_id : undefined,
      target.scope_kind === 'local_subflow' ? target.step_id : undefined,
    );

  const updateStep = (params: updateStepParams) => {
    let staticBindings: Record<string, string> | undefined;
    try {
      staticBindings = staticSelectionsForEdit(
        params.step_data,
        editingVersion?.static_bindings,
        staticSources?.results ?? [],
      );
    } catch (error) {
      notifyStepMutationError('select static data', error, reportLatestDraftChanged);
      return;
    }
    if (target.scope_kind === 'local_subflow' && target.subflow_id) {
      setPendingCount((count) => count + 1);
      void (async () => {
        const fields = Object.entries(params.step_data);
        const requestedFieldNames = fields.map(([fieldName]) => fieldName);
        const dataInputFieldNames = new Set(moduleCapabilities?.editable_data_input_fields ?? []);
        const scalarFieldNames = new Set(moduleCapabilities?.editable_fields ?? []);
        const requestedDataInputFields = requestedFieldNames.filter((fieldName) =>
          dataInputFieldNames.has(fieldName),
        );
        const requestedGuardedFields = requestedFieldNames.filter((fieldName) =>
          moduleCapabilities?.guarded_fields?.includes(fieldName),
        );
        const unsupportedFieldNames = requestedFieldNames.filter(
          (fieldName) =>
            !scalarFieldNames.has(fieldName) &&
            !dataInputFieldNames.has(fieldName) &&
            !moduleCapabilities?.guarded_fields?.includes(fieldName),
        );
        if (
          moduleCapabilitiesLoading ||
          !moduleCapabilities ||
          fields.length === 0 ||
          unsupportedFieldNames.length > 0
        ) {
          notifyStepMutationError(
            'update scoped module step',
            new Error(
              moduleCapabilitiesLoading
                ? 'Loading the scoped editing policy. Try again in a moment.'
                : unsupportedFieldNames.length > 0
                  ? `This module cannot edit ${unsupportedFieldNames.join(', ')} from the Automation Editor.`
                  : 'Choose at least one editable field for this module step.',
            ),
            reportLatestDraftChanged,
          );
          return;
        }
        const operations: CompositionOperation[] = [];
        const scalarFields = Object.fromEntries(
          fields.filter(([fieldName]) => scalarFieldNames.has(fieldName)),
        );
        if (Object.keys(scalarFields).length > 0) {
          operations.push({
            op_type: 'update_local_subflow_step_fields',
            subflow_id: target.subflow_id,
            step_id: params.step_id,
            fields: scalarFields,
          });
        }
        for (const fieldName of requestedDataInputFields) {
          const value = params.step_data[fieldName as keyof typeof params.step_data];
          operations.push({
            op_type: 'replace_data_input_group',
            subflow_id: target.subflow_id,
            step_id: params.step_id,
            field_name: fieldName,
            inputs: (Array.isArray(value) ? value : value ? [value] : []) as DataInput[],
          });
        }
        for (const fieldName of requestedGuardedFields) {
          try {
            const response = await apiClient.get<GuardedFieldResponse>(
              get_editor_local_subflow_guarded_field_url(
                automationId,
                target.subflow_id,
                params.step_id,
                fieldName as 'provided_code',
              ),
              { suppressGlobalErrorToast: true },
            );
            operations.push({
              op_type: 'set_authorable_field',
              subflow_id: target.subflow_id,
              step_id: params.step_id,
              field_name: fieldName,
              value: params.step_data[fieldName as keyof typeof params.step_data],
              expected_current_value_token: response.data.current_value_token,
            });
          } catch (error) {
            notifyStepMutationError(
              'prepare guarded module field update',
              error,
              reportLatestDraftChanged,
            );
            return;
          }
        }
        if (staticBindings) applyComposition(operations, { staticBindings });
        else applyComposition(operations);
      })().finally(() => setPendingCount((count) => count - 1));
      return;
    }
    setPendingCount((count) => count + 1);
    addToQueue({
      mutationFn: async (latestVersion) => {
        const response = await apiClient.put(
          get_update_step_url(latestVersion.id),
          {
            ...params,
            ...(staticBindings
              ? {
                  static_bindings: Object.fromEntries(
                    Object.entries(staticBindings).map(([id, revision]) => [
                      id,
                      latestVersion.static_bindings?.[id] ?? revision,
                    ]),
                  ),
                }
              : {}),
          },
          {
            suppressGlobalErrorToast: true,
          },
        );
        return response.data;
      },
      onSuccess: () => {},
      onError: (error) => {
        notifyStepMutationError('update step', error, reportLatestDraftChanged);
      },
      onComplete: () => {
        setPendingCount((count) => count - 1);
      },
    });
  };
  return { mutate: updateStep, isPending: pendingCount > 0 || isCompositionPending };
};

export default useUpdateStepAPI;
